import { BEACHES, type Beach } from '../data/beaches';
import type { ForecastData } from './openMeteo';
import { exposure, waveScore } from './scoring';

export interface BeachConditions {
  /** offshore significant wave height (m) */
  rawH: number | null;
  /** wave direction (deg) */
  dir: number | null;
  /** wave period (s) */
  per: number | null;
  /** exposure multiplier applied to rawH */
  exp: number;
  /** exposure-adjusted wave height (m) — what the beach actually gets */
  effH: number | null;
  score: number | null;
  wind: number | null;
  gust: number | null;
  wdir: number | null;
  temp: number | null;
}

export interface RankedBeach {
  beach: Beach;
  index: number;
  conditions: BeachConditions;
}

export function beachAt(data: ForecastData, i: number, t: number): BeachConditions {
  const mh = data.marine[i]?.hourly;
  const wh = data.weather[i]?.hourly;
  const rawH = mh?.wave_height?.[t] ?? null;
  const dir = mh?.wave_direction?.[t] ?? null;
  const exp = exposure(dir, BEACHES[i].facing);
  const effH = rawH == null ? null : rawH * exp;
  return {
    rawH,
    dir,
    per: mh?.wave_period?.[t] ?? null,
    exp,
    effH,
    score: waveScore(effH),
    wind: wh?.wind_speed_10m?.[t] ?? null,
    gust: wh?.wind_gusts_10m?.[t] ?? null,
    wdir: wh?.wind_direction_10m?.[t] ?? null,
    temp: wh?.temperature_2m?.[t] ?? null,
  };
}

export function rankedAt(data: ForecastData, t: number): RankedBeach[] {
  return BEACHES.map((beach, index) => ({ beach, index, conditions: beachAt(data, index, t) }))
    .sort((a, z) => (z.conditions.score ?? -1) - (a.conditions.score ?? -1));
}

/**
 * Best score any beach reaches at each hour of the forecast. One pass feeds
 * both the day chips (daily peak) and the hour band under the scrubber, so the
 * 168 × 16 lookups happen once per load rather than per control.
 */
export function hourlyBest(data: ForecastData): (number | null)[] {
  return data.times.map((_, t) => {
    let best: number | null = null;
    for (let i = 0; i < BEACHES.length; i++) {
      const s = beachAt(data, i, t).score;
      if (s != null && (best == null || s > best)) best = s;
    }
    return best;
  });
}

/** peak score per calendar day, daylight hours only — matches what `bestWindow` searches */
export function dailyPeaks(times: string[], best: (number | null)[]): Map<string, number | null> {
  const peaks = new Map<string, number | null>();
  times.forEach((iso, t) => {
    const hr = +iso.slice(11, 13);
    if (hr < DAY_START || hr > DAY_END) return;
    const day = iso.slice(0, 10);
    const s = best[t];
    const cur = peaks.get(day) ?? null;
    if (s != null && (cur == null || s > cur)) peaks.set(day, s);
    else if (!peaks.has(day)) peaks.set(day, cur);
  });
  return peaks;
}

/** daylight window used by both the best-window finder and the day peaks */
export const DAY_START = 7;
export const DAY_END = 19;

export interface BestWindow {
  /** time index into data.times */
  t: number;
  /** index into BEACHES */
  i: number;
  score: number;
}

/**
 * Best *fun* window: highest score across future daylight hours, with anything
 * above 8.5 mirrored back down so the finder never recommends danger.
 */
export function bestWindow(data: ForecastData): BestWindow | null {
  let bestVal = -1;
  let best: BestWindow | null = null;
  const now = Date.now();

  for (let t = 0; t < data.times.length; t++) {
    const hr = +data.times[t].slice(11, 13);
    if (hr < DAY_START || hr > DAY_END) continue; // daylight only
    if (new Date(data.times[t]).getTime() < now) continue; // future only
    for (let i = 0; i < BEACHES.length; i++) {
      const { score } = beachAt(data, i, t);
      if (score == null) continue;
      const funVal = score > 8.5 ? 8.5 - (score - 8.5) : score; // penalize danger zone
      if (funVal > bestVal) {
        bestVal = funVal;
        best = { t, i, score };
      }
    }
  }
  return best;
}
